import { useState } from 'react';
import { ChanceRing, Mark, ResultChip, ScratchedTag } from '../primitives';
import { money } from '../lib/format';
import { Agreement } from './RunnerCard';
import { RunnerDetail } from './RunnerDetail';
import { STANDOUT_LABEL, STANDOUT_TITLE } from './standout';
import {
  BUCKET_LABEL,
  chanceOf,
  fieldRows,
  positionOf,
  sortField,
  type FieldRow,
  type FieldRunner,
  type FieldSort,
  type Race,
  type RaceResults,
  type Scratching,
} from './lib';

export const FIELD_SORTS: { value: FieldSort; label: string }[] = [
  { value: 'number', label: 'No.' },
  { value: 'odds', label: 'Odds' },
  { value: 'barrier', label: 'Barrier' },
  { value: 'settle', label: 'Settling' },
];

/** Announces the Field view on the tips page until the viewer clears it or tries it. */
export function FieldNews({ onTry, onDismiss }: { onTry: () => void; onDismiss: () => void }) {
  return (
    <div className="ui-news" role="region" aria-label="New: the Field view">
      <span className="tx">
        <b>New: the Field view.</b> Every runner in each race on one line, with form, jockey and the market, and our tips marked in
        their bet colour.
      </span>
      <button type="button" className="go" onClick={onTry}>
        Show the field
      </button>
      <button type="button" className="x" aria-label="Dismiss" onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}

/** One runner on one line; tapping it opens the full form (and our analysis, for a tip). */
function FieldLine({ row, pending, onToggle }: { row: FieldRow; pending: boolean; onToggle?: (row: FieldRow, open: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const p = row.pick;
  const chance = p && !row.scratched ? chanceOf(p.sel) : null;
  const facts = [
    row.trainer && `Trainer ${row.trainer}`,
    row.barrier && `Barrier ${row.barrier}`,
    row.weight && `${row.weight}kg`,
    row.bucket && `Settles ${(BUCKET_LABEL[row.bucket] ?? row.bucket).toLowerCase()}`,
  ].filter(Boolean);
  const cls = [p ? `pk ${p.bet}` : '', row.scratched ? 'scr' : ''].filter(Boolean).join(' ');
  const pos = positionOf(row.result);
  // ResultChip draws nothing for a missing or placeholder position, so neither does its line
  const finished = !pending && pos != null && pos > 0 && pos !== 999;
  const standout = !!p?.sel.isStandardClaim && !row.scratched;

  return (
    <li
      className={cls || undefined}
      data-open={open}
      data-standout={standout || undefined}
      title={standout ? STANDOUT_TITLE : undefined}
    >
      <button
        type="button"
        className="fr"
        aria-expanded={row.scratched ? undefined : open}
        disabled={row.scratched}
        onClick={() => {
          setOpen(!open);
          onToggle?.(row, !open);
        }}
      >
        <span className="sc" aria-label={`Number ${row.number}`}>
          <b>{row.number}</b>
        </span>
        <span className="mn">
          <span className="l1">
            <span className="hn">{row.horse}</span>
            {standout && <span className="ui-vh">{STANDOUT_LABEL}</span>}
            {/* the barrier in brackets after the name, the way form guides print it */}
            {row.barrier != null && (
              <span className="br" aria-label={`barrier ${row.barrier}`}>
                ({row.barrier})
              </span>
            )}
            {/* Pick 1's agreement pips, as on its card */}
            {p?.kind === 'top' && p.sel.pick_order === 1 && !row.scratched && p.sel.topPickAgreement && (p.sel.topPickSlots ?? 0) > 1 && (
              <Agreement agree={p.sel.topPickAgreement} of={p.sel.topPickSlots!} />
            )}
          </span>
          {/* tags on a line of their own - pick tag, mark, then the result or scratching - so the name
              line is only ever the name; a runner with none of them (not a tip, not yet run) gets no line */}
          {(row.scratched || finished || p) && (
            <span className="l3">
              {p && !row.scratched && <span className="pt">{p.label}</span>}
              {p?.sel.isConsensusPick && !row.scratched && <Mark iconOnly />}
              {row.scratched ? <ScratchedTag /> : finished && <ResultChip pos={pos} sp={row.result?.price_sp} place={row.result?.price_place} />}
            </span>
          )}
          <span className="l2">
            <span className="f10">{row.form.horse_last10 || '–'}</span>
            {row.jockey && <span>{row.jockey}</span>}
          </span>
        </span>
        <span className="od">
          <b>{row.scratched ? '' : money(row.odds)}</b>
          {row.move && !row.scratched && (
            // the arrow and the opening price, struck through: "was $4.00, now the price above"
            <small className={row.dir} title={row.move} aria-label={row.move}>
              {row.move.split(' ')[0]}
              {row.opening != null && <s>{money(row.opening)}</s>}
            </small>
          )}
        </span>
        <span className="ch">{chance != null && <ChanceRing pct={chance} />}</span>
      </button>
      {open && !row.scratched && (
        <div className="fx">
          {facts.length > 0 && <div className="fa">{facts.join(' · ')}</div>}
          {p && (
            <div className="fa">
              {p.label}: our price <b>{money(p.sel.assessedOdds)}</b>
              {chance != null && (
                <>
                  {' '}
                  · chance <b>{Math.round(chance)}%</b>
                </>
              )}
            </div>
          )}
          <RunnerDetail form={row.form} stake={p?.sel.bet_stake} analysis={p?.sel.analysis} />
        </div>
      )}
    </li>
  );
}

/**
 * Field view: every runner in the race on one compact line each. Our tips keep their bet
 * colour and chance ring; the rest show form, jockey and the market. Before the field has
 * loaded (or if it cannot be), the race still lists our tips. The Compact view is the same
 * rows with no runners passed in, so it lists only our tips.
 */
export function RaceField({
  race,
  runners,
  loading,
  failed = false,
  scratchings,
  result,
  ran,
  sort,
  onToggle,
  name = 'field',
}: {
  race: Race;
  runners: FieldRunner[];
  loading: boolean;
  /** The field could not be loaded, so only our tips are listed. */
  failed?: boolean;
  scratchings: Scratching[];
  result: RaceResults | undefined;
  ran: boolean;
  sort: FieldSort;
  onToggle?: (row: FieldRow, open: boolean) => void;
  /** What the list is, for screen readers: 'field', or 'tips' in the Compact view. */
  name?: string;
}) {
  const rows = sortField(fieldRows(race, runners, scratchings, result), sort);
  if (!rows.length) return loading ? <div className="ui-fld-note">Loading the field…</div> : null;
  return (
    <div className="ui-fld" data-tour="field">
      <div className="fh" aria-hidden="true">
        <span>No.</span>
        <span>Horse · form · jockey</span>
        <span>Odds</span>
        <span>Chance</span>
      </div>
      <ul aria-label={`Race ${race.race_number} ${name}`}>
        {rows.map((r) => (
          <FieldLine key={r.number} row={r} pending={!ran} onToggle={onToggle} />
        ))}
      </ul>
      {loading && <div className="ui-fld-note">Loading the rest of the field…</div>}
      {failed && !loading && !runners.length && <div className="ui-fld-note">We couldn't load the rest of the field, so only our tips are listed.</div>}
    </div>
  );
}
